/**
 * IntakyFirmPage — public, firm-facing lead activity dashboard.
 * Route: /external/intake/:firmId
 *
 * Layout mirrors Flighty airports exactly:
 *   - Map: position:fixed; full-bleed (z-index 0)
 *   - Black scrim: position:fixed; opacity driven by scrollY (z-index 1)
 *   - Header: position:fixed; 72px; transparent→frosted on scroll (z-index 90)
 *   - Scroll content (pointer-events:none on wrapper):
 *       1. min-h:50vh transparent spacer
 *       2. Bottom-sheet card (pointer-events:auto):
 *            rounded-t-3xl, Flighty multi-layer shadow, drag handle, title, stats, table
 *
 * Loading screen: cinematic reveal — shows Finch logo + progress text while
 * map tiles + data + SSE all initialize, then crossfades to the live dashboard.
 *
 * Reconnection: detects lid-close / tab-hide via visibilitychange, shows banner.
 */
import { MagnifyingGlassIcon, TvIcon } from '@heroicons/react/20/solid';
import confetti from 'canvas-confetti';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { INTAKY_COLORS, INTAKY_FONT, statusToTier, statusToTierConfig } from '../components/intaky/intaky-tokens';
import { IntakyActivityDrawer } from '../components/intaky/IntakyActivityDrawer';
import type { IntakyActivityRow } from '../components/intaky/IntakyActivityTable';
import { IntakyGlobalActivityDrawer } from '../components/intaky/IntakyGlobalActivityDrawer';
import { IntakyIntakeTable } from '../components/intaky/IntakyIntakeTable';
import type { IntakyMapHandle, IntakyMapIntake, MapTooltip } from '../components/intaky/IntakyMap';
import { IntakyMap } from '../components/intaky/IntakyMap';

// Mirrors app/defs/intake_activity.py IntakeSnapshot
interface IntakeSnapshot {
  status: string;
  client_display: string;
  case_type: string;
  referral_source: string;
  city: string;
  state: string;
  location_precision: 'city' | 'state' | 'none';
}

// ─── Constants ────────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_APP_DJANGO_BACKEND_HOST as string;
const HEADER_H = 72; // px — matches Flighty's min-h-[72px]

// Flighty's exact bottom-sheet shadow
const BOTTOM_SHEET_SHADOW = [
  '0 1px 1px .5px rgba(0,0,0,0.06)',
  '0 3px 3px 1.5px rgba(0,0,0,0.06)',
  '0 6px 6px -3px rgba(0,0,0,0.06)',
  '0 12px 12px -6px rgba(0,0,0,0.06)',
  '0 24px 24px -12px rgba(0,0,0,0.06)',
].join(', ');

// SSE connection states
type SSEState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

// ─── Types ────────────────────────────────────────────────────────────────────

interface OverviewResponse {
  firm_name: string;
  total_active: number;
  intakes: IntakyMapIntake[];
  activities: IntakyActivityRow[];
}

// ─── Loading Screen ──────────────────────────────────────────────────────────

interface LoadingScreenProps {
  visible: boolean;
  loadingText: string;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ visible, loadingText }) => (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      background: '#000000',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: INTAKY_FONT.family,
      transition: 'opacity 0.8s ease-out',
      opacity: visible ? 1 : 0,
      pointerEvents: visible ? 'auto' : 'none',
    }}
  >
    {/* Logo — white on black, clean and premium */}
    <div
      style={{
        marginBottom: 32,
        animation: 'intaky-logo-pulse 2.5s ease-in-out infinite',
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="38" viewBox="0 0 31 24" fill="none">
        <path fillRule="evenodd" clipRule="evenodd" d="M17.4232 5.41484C16.6753 4.3061 16.6692 4.31217 15.7284 5.25292L15.7284 5.25293L0.676797 20.1894C-0.24242 21.1086 -0.24237 21.1087 0.781835 21.8377L0.797857 21.8491C1.06602 22.04 1.3416 22.2189 1.62386 22.3853L8.56262 15.4466L8.77013 16.5808L2.49905 22.8518C2.84577 23.0179 3.20066 23.1666 3.56253 23.2974L8.56262 18.2973L8.77013 19.4315L4.5864 23.6153C6.19334 24.0345 7.90833 24.1176 9.63516 23.8326C11.0879 23.5897 12.4599 23.0634 13.7108 22.2943C19.8042 18.6108 21.055 10.7985 17.4232 5.41484Z" fill="white" />
        <path d="M30.3223 6.39559C29.7574 5.82891 29.7574 5.66698 29.717 5.50507C29.2731 2.34776 26.5292 0 23.3413 0C21.6867 0 20.1534 0.607178 18.9428 1.74057L18.055 2.63109C17.1425 3.50242 17.3853 3.18751 17.9743 3.96689C21.7674 8.98622 21.6062 16.1105 17.4497 20.9274C22.2762 22.0709 27.8608 16.4747 27.8608 10.8077C27.8608 9.87674 27.7397 8.94574 27.4976 8.05522C27.4169 7.81235 27.4976 7.56947 27.6187 7.36708C27.7801 7.16469 27.9819 7.04326 28.2643 7.04326H30.0802C30.914 7.04326 30.9131 6.98814 30.3223 6.39559Z" fill="white" />
      </svg>
    </div>

    {/* Loading text */}
    <p
      style={{
        color: 'rgba(255,255,255,0.45)',
        fontSize: 13,
        fontWeight: 500,
        letterSpacing: '0.04em',
        position: 'relative',
      }}
    >
      {loadingText}
    </p>

    {/* Minimal progress line */}
    <div
      style={{
        width: 80,
        height: 1,
        borderRadius: 1,
        background: 'rgba(255,255,255,0.06)',
        marginTop: 24,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          height: '100%',
          borderRadius: 1,
          background: 'rgba(255,255,255,0.4)',
          animation: 'intaky-progress-sweep 1.8s ease-in-out infinite',
        }}
      />
    </div>
  </div>
);

// ─── Reconnection Banner ─────────────────────────────────────────────────────

interface ReconnectionBannerProps {
  sseState: SSEState;
  onRefresh: () => void;
  failedAttempts: number;
}

const ReconnectionBanner: React.FC<ReconnectionBannerProps> = ({
  sseState,
  onRefresh,
  failedAttempts,
}) => {
  if (sseState === 'connected' || sseState === 'connecting') return null;

  const showRefreshButton = failedAttempts >= 3;

  return (
    <div
      style={{
        position: 'fixed',
        top: HEADER_H + 8,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 100,
        background: sseState === 'disconnected' ? 'rgba(15,23,42,0.92)' : 'rgba(59,130,246,0.92)',
        backdropFilter: 'blur(12px)',
        color: 'white',
        borderRadius: 12,
        padding: '10px 20px',
        fontSize: 13,
        fontWeight: 600,
        fontFamily: INTAKY_FONT.family,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
        animation: 'intaky-row-in 0.3s ease-out',
      }}
    >
      {sseState === 'reconnecting' && (
        <div
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            border: '2px solid rgba(255,255,255,0.3)',
            borderTopColor: 'white',
            animation: 'intaky-spin 0.75s linear infinite',
            flexShrink: 0,
          }}
        />
      )}
      <span>
        {sseState === 'reconnecting' ? 'Reconnecting...' : 'Connection lost'}
      </span>
      {showRefreshButton && (
        <button
          type="button"
          onClick={onRefresh}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: 6,
            color: 'white',
            fontSize: 12,
            fontWeight: 700,
            padding: '4px 12px',
            cursor: 'pointer',
            fontFamily: INTAKY_FONT.family,
          }}
        >
          Refresh
        </button>
      )}
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

interface IntakyFirmPageProps {
  /** Override the overview API URL (e.g. for the all-firms view). */
  overviewUrl?: string;
  /** Override the SSE stream URL (e.g. for the all-firms view). */
  streamUrl?: string;
}

export const IntakyFirmPage: React.FC<IntakyFirmPageProps> = ({ overviewUrl, streamUrl }) => {
  const { firmId } = useParams<{ firmId: string }>();

  const resolvedOverviewUrl = overviewUrl ?? `${API_BASE}/api/intaky/${firmId}/overview/`;
  const resolvedStreamUrl = streamUrl ?? `${API_BASE}/api/intaky/${firmId}/stream/`;

  const [firmName, setFirmName] = useState('');
  const [intakes, setIntakes] = useState<IntakyMapIntake[]>([]);
  const [sseState, setSseState] = useState<SSEState>('connecting');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flashedIntakeIds, setFlashedIntakeIds] = useState<Set<string>>(new Set());
  const [mapTooltips, setMapTooltips] = useState<MapTooltip[]>([]);
  const [drawerIntake, setDrawerIntake] = useState<IntakyMapIntake | null>(null);
  const [showGlobalActivity, setShowGlobalActivity] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIndex, setSearchIndex] = useState(0);
  const [allActivities, setAllActivities] = useState<IntakyActivityRow[]>([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  // Increments on every SSE activity — drawer watches this to refetch
  const [activityVersion, setActivityVersion] = useState(0);
  const [lastUpdatedText, setLastUpdatedText] = useState('');
  // scrollY as a raw number for smooth opacity calculation
  const [scrollY, setScrollY] = useState(0);
  // stitched = card has touched the navbar → triggers CSS transition to expand
  const [stitched, setStitched] = useState(false);
  // Loading screen gates
  const [mapReady, setMapReady] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const [sseReady, setSseReady] = useState(false);
  const [loadingText, setLoadingText] = useState('Connecting to your pipeline...');

  // Cha-ching sound + mute + confetti
  const [muted, setMuted] = useState(() => localStorage.getItem('intaky-muted') === '1');
  const mutedRef = useRef(muted);
  const chachingRef = useRef<HTMLAudioElement | null>(null);
  const mapHandleRef = useRef<IntakyMapHandle>(null);
  // Celebration overlay: shows the dot above the scrim during confetti
  const [celebration, setCelebration] = useState<{
    origin: { x: number; y: number };
    clientName: string;
    tier: string;
  } | null>(null);

  const failedAttemptsRef = useRef(0);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const seenRef = useRef(new Set<string>());
  const esRef = useRef<EventSource | null>(null);

  // Loading screen visible until all 3 signals fire
  const showLoadingScreen = !mapReady || !dataReady || !sseReady;

  // ── Preload cha-ching audio ───────────────────────────────────────────────
  useEffect(() => {
    const audio = new Audio('/audio/cha-ching.mp3');
    audio.preload = 'auto';
    audio.volume = 0.7;
    chachingRef.current = audio;
  }, []);

  // Keep mutedRef in sync
  useEffect(() => {
    mutedRef.current = muted;
    localStorage.setItem('intaky-muted', muted ? '1' : '0');
  }, [muted]);

  // ── Last updated ticker ──────────────────────────────────────────────────
  useEffect(() => {
    if (!lastUpdatedAt) return undefined;
    const tick = () => {
      const diff = (Date.now() - lastUpdatedAt.getTime()) / 1000;
      if (diff < 5) setLastUpdatedText('Updated just now');
      else if (diff < 60) setLastUpdatedText(`Updated ${Math.floor(diff)}s ago`);
      else if (diff < 3600) setLastUpdatedText(`Updated ${Math.floor(diff / 60)}m ago`);
      else setLastUpdatedText(`Updated ${Math.floor(diff / 3600)}h ago`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [lastUpdatedAt]);

  // ── Scroll tracking ────────────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrollY(y);
      // Threshold: card top (50vh) reaches navbar bottom (HEADER_H)
      const trigger = window.innerHeight * 0.5 - HEADER_H;
      setStitched(y >= trigger);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ── ⌘K shortcut to open search ──────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
      if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
        setSearchQuery('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchOpen]);

  // Map scrim: subtle darkening as card scrolls up
  const scrimOpacity = Math.min(0.15, scrollY / 500);

  // stitched drives both header and card — CSS transition does the animation
  const headerProgress = stitched ? 1 : 0;
  const cardMargin = stitched ? 0 : 24;
  const cardRadius = stitched ? 0 : 24;

  // ── Data load ─────────────────────────────────────────────────────────────
  useEffect(() => {
    setLoadingText('Connecting to your pipeline...');
    fetch(resolvedOverviewUrl)
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json() as Promise<OverviewResponse>;
      })
      .then((data) => {
        setFirmName(data.firm_name);
        // Only load active leads — closed ones (rejected/archived/lost) stay off
        // until they appear via SSE status change during the session
        const CLOSED_STATUSES = new Set(['rejected', 'archived', 'lost']);
        const activeIntakes = data.intakes.filter(
          (i: IntakyMapIntake) => !CLOSED_STATUSES.has(i.status),
        );
        setIntakes(activeIntakes);
        data.activities.forEach((a: IntakyActivityRow) => seenRef.current.add(a.id));
        setAllActivities(data.activities);
        setLoading(false);
        setDataReady(true);
        setLoadingText(
          activeIntakes.length > 0
            ? `${activeIntakes.length} leads loaded. Going live...`
            : 'Going live...',
        );
        if (data.activities.length > 0) {
          setLastUpdatedAt(new Date(data.activities[0].occurred_at));
        }
      })
      .catch((e) => {
        setError(`Could not load data: ${e.message}`);
        setLoading(false);
        setDataReady(true); // dismiss loading screen even on error
      });
  }, [resolvedOverviewUrl]);

  // ── SSE stream ────────────────────────────────────────────────────────────
  const connectSSE = useCallback(() => {
    if (esRef.current) return;
    setSseState('reconnecting');
    const es = new EventSource(resolvedStreamUrl, {
      withCredentials: true,
    });
    esRef.current = es;
    es.onopen = () => {
      setSseState('connected');
      setSseReady(true);
      failedAttemptsRef.current = 0;
      setFailedAttempts(0);
    };

    es.onmessage = (e) => {
      try {
        const activity = JSON.parse(e.data) as IntakyActivityRow & {
          intake_snapshot?: IntakeSnapshot | null;
        };
        if (seenRef.current.has(activity.id)) return;
        seenRef.current.add(activity.id);

        // Update last-updated timestamp + bump version for drawer refresh
        setLastUpdatedAt(new Date(activity.occurred_at));
        setActivityVersion((v) => v + 1);

        const snap = activity.intake_snapshot;

        setIntakes((prev) => {
          let transitionedToSigned = false;
          const exists = prev.some((i) => i.id === activity.intake_id);

          let next: IntakyMapIntake[];

          if (!exists && snap) {
            // New intake — add it to the list (don't add closed ones)
            const CLOSED = new Set(['rejected', 'archived', 'lost']);
            if (CLOSED.has(snap.status)) return prev;
            next = [
              {
                id: activity.intake_id,
                client_display: snap.client_display,
                status: snap.status,
                state: snap.state,
                city: snap.city,
                location_precision: snap.location_precision,
                case_type: snap.case_type,
                referral_source: snap.referral_source,
                last_activity_headline: activity.headline,
                last_activity_detail: activity.detail || '',
                last_activity_at: activity.occurred_at,
              },
              ...prev,
            ];
            if (snap.status === 'signed') transitionedToSigned = true;
          } else {
            next = prev.map((intake) => {
              if (intake.id !== activity.intake_id) return intake;
              if (snap?.status === 'signed' && intake.status !== 'signed') {
                transitionedToSigned = true;
              }
              return {
                ...intake,
                last_activity_headline: activity.headline,
                last_activity_detail: activity.detail || '',
                last_activity_at: activity.occurred_at,
                ...(snap && {
                  status: snap.status,
                  client_display: snap.client_display,
                  case_type: snap.case_type,
                  referral_source: snap.referral_source,
                  city: snap.city,
                  state: snap.state,
                  location_precision: snap.location_precision,
                }),
              };
            });
          }

          // 🎉 Cha-ching + confetti! Only on actual status TRANSITION to signed
          if (transitionedToSigned) {
            // Sound (respects mute)
            if (!mutedRef.current) {
              const audio = chachingRef.current;
              if (audio) {
                audio.currentTime = 0;
                audio.play().catch(() => {});
              }
            }

            // Get dot's screen position — snapshot it NOW before annotations re-render
            const origin = mapHandleRef.current?.getViewportOrigin(activity.intake_id)
              ?? { x: 0.5, y: 0.25 };

            // Show celebration overlay (scrim + dot on top)
            setCelebration({
              origin,
              clientName: snap?.client_display ?? 'New Lead',
              tier: 'signed',
            });
            setTimeout(() => setCelebration(null), 3500);

            // 360° confetti burst from the dot — 6 directional bursts over 3s
            const colors = ['#22C55E', '#4ADE80', '#FFD700', '#FFF', '#86EFAC'];
            const burstCount = 6;
            Array.from({ length: burstCount }).forEach((_, i) => {
              setTimeout(() => {
                confetti({
                  particleCount: 30,
                  angle: (360 / burstCount) * i,
                  spread: 60,
                  origin,
                  colors,
                  gravity: 0.8,
                  scalar: 1.0,
                  ticks: 180,
                  startVelocity: 30,
                });
              }, i * 400);
            });

            // Big center burst at start
            confetti({
              particleCount: 100,
              spread: 360,
              origin,
              colors,
              gravity: 0.6,
              scalar: 1.1,
              ticks: 200,
              startVelocity: 35,
            });
          }

          return next;
        });

        // Prepend to global activity list
        setAllActivities((prev) => [activity, ...prev].slice(0, 200));

        // Flash the table row
        setFlashedIntakeIds((prev) => new Set([...prev, activity.intake_id]));
        setTimeout(() => {
          setFlashedIntakeIds((prev) => {
            const next = new Set(prev);
            next.delete(activity.intake_id);
            return next;
          });
        }, 3000);

        // Show chat bubble tooltip on the map (stack if same intake)
        setMapTooltips((prev) => {
          const existing = prev.find((t) => t.intakeId === activity.intake_id);
          const newCount = (existing?.stackCount ?? 0) + 1;
          return [
            ...prev.filter((t) => t.intakeId !== activity.intake_id),
            {
              intakeId: activity.intake_id,
              headline: activity.headline,
              detail: activity.detail || undefined,
              tier: statusToTier(snap?.status ?? 'in_progress'),
              clientName: snap?.client_display ?? activity.intake_id.slice(0, 8),
              stackCount: newCount,
            },
          ];
        });
      } catch {
        // ignore parse errors
      }
    };

    es.onerror = () => {
      setSseState('disconnected');
      esRef.current?.close();
      esRef.current = null;
      failedAttemptsRef.current += 1;
      setFailedAttempts(failedAttemptsRef.current);
      // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
      const delay = Math.min(30000, 1000 * 2 ** (failedAttemptsRef.current - 1));
      setSseState('reconnecting');
      setTimeout(connectSSE, delay);
    };
  }, [resolvedStreamUrl]);

  useEffect(() => {
    connectSSE();
    return () => {
      esRef.current?.close();
      esRef.current = null;
    };
  }, [connectSSE]);

  // ── Visibility change: force reconnect when tab becomes visible ──────────
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // If disconnected or stale, force reconnect immediately
        if (!esRef.current || esRef.current.readyState === EventSource.CLOSED) {
          esRef.current?.close();
          esRef.current = null;
          failedAttemptsRef.current = 0;
          setFailedAttempts(0);
          connectSSE();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [connectSSE]);

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  // ── Loading / error screens ───────────────────────────────────────────────
  if (loading && !dataReady) {
    // Pure loading — show the cinematic loading screen only
    return (
      <div style={{ fontFamily: INTAKY_FONT.family }}>
        <LoadingScreen visible loadingText={loadingText} />
        {/* Extra keyframes for loading screen */}
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,400;0,14..32,500;0,14..32,600;0,14..32,700;0,14..32,800&display=swap');
          @keyframes intaky-spin { to { transform: rotate(360deg); } }
          @keyframes intaky-logo-pulse {
            0%, 100% { transform: scale(1); box-shadow: 0 0 60px rgba(59,130,246,0.3), 0 0 120px rgba(34,197,94,0.15); }
            50% { transform: scale(1.05); box-shadow: 0 0 80px rgba(59,130,246,0.4), 0 0 160px rgba(34,197,94,0.25); }
          }
          @keyframes intaky-grid-drift {
            from { transform: translate(0, 0); }
            to { transform: translate(40px, 40px); }
          }
          @keyframes intaky-progress-sweep {
            0% { width: 0%; margin-left: 0; }
            50% { width: 60%; margin-left: 20%; }
            100% { width: 0%; margin-left: 100%; }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={S.centeredScreen}>
        <p style={{ color: '#DC2626', fontSize: INTAKY_FONT.sizeMd }}>{error}</p>
      </div>
    );
  }

  // Text color: white over map, transitions to near-black once header solidifies
  const headerTextColor = headerProgress < 0.5
    ? `rgba(255,255,255,${0.95 - headerProgress * 0.4})`
    : INTAKY_COLORS.textPrimary;

  const connected = sseState === 'connected';

  // Connected dot color (always visible)
  const connectedColor = connected ? INTAKY_COLORS.liveGreen : '#94A3B8';

  return (
    <div
      style={{
        fontFamily: INTAKY_FONT.family,
        color: INTAKY_COLORS.textPrimary,
        width: '100%',
        minHeight: '100vh',
        overflowX: 'hidden',
      }}
    >
      {/* ── Loading screen overlay (fades out once everything is ready) ── */}
      <LoadingScreen visible={showLoadingScreen} loadingText={loadingText} />

      {/* ── Reconnection banner ── */}
      <ReconnectionBanner
        sseState={sseState}
        onRefresh={handleRefresh}
        failedAttempts={failedAttempts}
      />

      {/* ── Global styles ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,400;0,14..32,500;0,14..32,600;0,14..32,700;0,14..32,800&display=swap');

        html, body { margin: 0; padding: 0; }
        *, *::before, *::after { box-sizing: border-box; }

        @keyframes intaky-ping {
          0%   { transform: scale(1); opacity: 0.5; }
          80%  { transform: scale(2.6); opacity: 0; }
          100% { transform: scale(2.6); opacity: 0; }
        }
        @keyframes intaky-wiggle {
          0%, 100% { transform: translate(-50%,-50%) scale(1); opacity: 0.85; }
          50%       { transform: translate(-50%,-50%) scale(1.22); opacity: 0.35; }
        }
        @keyframes intaky-row-in {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes intaky-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes intaky-logo-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes intaky-grid-drift {
          from { transform: translate(0, 0); }
          to { transform: translate(40px, 40px); }
        }
        @keyframes intaky-progress-sweep {
          0% { width: 0%; margin-left: 0; }
          50% { width: 60%; margin-left: 20%; }
          100% { width: 0%; margin-left: 100%; }
        }
        @keyframes intaky-tooltip-in {
          from { opacity: 0; transform: translateY(8px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes intaky-dot-pop {
          0%   { transform: scale(0); opacity: 0; }
          60%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes intaky-ticker-bounce {
          0%, 15%  { transform: translateX(0); }
          45%, 55% { transform: translateX(var(--ticker-offset, 0)); }
          85%, 100% { transform: translateX(0); }
        }
        @keyframes intaky-drawer-slide {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
        @keyframes intaky-search-backdrop {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes intaky-search-in {
          from { opacity: 0; transform: scale(0.98) translateY(-4px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .intaky-dot-hover:hover .intaky-dot-ring {
          opacity: 1 !important;
        }
        .intaky-table-row:hover {
          background: #F1F5F9 !important;
        }
        @media (min-width: 768px) {
          .intaky-cmd-k { display: inline-block !important; }
          .intaky-tv-label { display: inline !important; }
        }
        .intaky-tv-btn:hover {
          background: linear-gradient(to bottom, rgba(39,40,47,0.9), rgba(13,12,15,0.9)) !important;
        }
        .intaky-tv-label { display: none; }
        @keyframes intaky-updated-pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }

        /* Custom scrollbar — matches app-wide .scrollbar-thin style */
        html::-webkit-scrollbar {
          width: 4px;
        }
        html::-webkit-scrollbar-track {
          background: transparent;
        }
        html::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.12);
          border-radius: 100vh;
        }
        html::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.25);
        }
        html {
          scrollbar-width: thin;
          scrollbar-color: rgba(0, 0, 0, 0.12) transparent;
        }

        /* MapKit attribution — sit just above the card top border */
        .mk-controls-container {
          bottom: calc(50vh + 2px) !important;
        }

        /* Leaflet overrides */
        .leaflet-control-attribution {
          font-family: Inter, system-ui, sans-serif !important;
          font-size: 10px !important;
          opacity: 0.45 !important;
          background: rgba(255,255,255,0.65) !important;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 14px !important;
          box-shadow: 0 8px 32px rgba(0,0,0,0.16) !important;
          font-family: Inter, system-ui, sans-serif !important;
        }
        .leaflet-popup-tip-container { display: none !important; }
        .leaflet-control-zoom {
          border: none !important;
          box-shadow: 0 2px 10px rgba(0,0,0,0.14) !important;
          border-radius: 10px !important;
          overflow: hidden;
        }
        .leaflet-control-zoom a {
          color: ${INTAKY_COLORS.textPrimary} !important;
          font-size: 18px !important;
          line-height: 30px !important;
        }
        .leaflet-div-icon { background: transparent !important; border: none !important; }
        .leaflet-marker-icon { overflow: visible !important; }
      `}</style>

      {/* ══ FIXED MAP — full bleed behind everything ════════════════════════════ */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 0,
        }}
      >
        <IntakyMap
          ref={mapHandleRef}
          intakes={intakes}
          tooltips={mapTooltips}
          onDismissTooltip={(intakeId) =>
            setMapTooltips((prev) => prev.filter((t) => t.intakeId !== intakeId))
          }
          onMapReady={() => setMapReady(true)}
        />

        {/* Mute button — same row as Apple Maps attribution (bottom of visible map half) */}
        <button
          type="button"
          onClick={() => setMuted((prev) => !prev)}
          aria-label={muted ? 'Unmute sounds' : 'Mute sounds'}
          style={{
            position: 'absolute',
            bottom: 'calc(50vh + 4px)',
            right: 24,
            zIndex: 10,
            width: 32,
            height: 32,
            borderRadius: 8,
            border: 'none',
            background: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 15,
            color: muted ? INTAKY_COLORS.textMuted : INTAKY_COLORS.textPrimary,
            transition: 'color 0.2s ease',
          }}
        >
          {muted ? '🔇' : '🔊'}
        </button>
      </div>

      {/* ══ FIXED BLACK SCRIM — Flighty's scroll-driven map darkening ══════════ */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'black',
          opacity: scrimOpacity,
          zIndex: 1,
          pointerEvents: 'none',
          transition: 'opacity 0.1s linear',
        }}
      />

      {/* ══ CELEBRATION OVERLAY — scrim + dot spotlight on signed transition ═══ */}
      {celebration && (
        <>
          {/* Dark scrim behind everything */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'black',
              opacity: 0.45,
              zIndex: 95,
              pointerEvents: 'none',
              animation: 'intaky-tooltip-in 0.3s ease-out',
            }}
          />
          {/* The celebrating dot rendered ABOVE the scrim */}
          <div
            style={{
              position: 'fixed',
              left: `${celebration.origin.x * 100}%`,
              top: `${celebration.origin.y * 100}%`,
              transform: 'translate(-50%, -50%)',
              zIndex: 96,
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              animation: 'intaky-dot-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) both',
            }}
          >
            {/* Green glowing dot */}
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: 'linear-gradient(145deg, #22C55E, #4ADE80)',
                outline: '3px solid white',
                outlineOffset: -1,
                boxShadow: '0 0 30px rgba(34,197,94,0.5), 0 0 60px rgba(34,197,94,0.25), 0 2px 8px rgba(0,0,0,0.3)',
              }}
            />
            {/* Client name pill */}
            <div
              style={{
                marginTop: 6,
                background: 'white',
                borderRadius: 10,
                padding: '4px 12px',
                fontSize: 12,
                fontWeight: 700,
                color: '#0F172A',
                fontFamily: INTAKY_FONT.family,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                whiteSpace: 'nowrap',
              }}
            >
              {celebration.clientName} — Signed! 🎉
            </div>
          </div>
        </>
      )}

      {/* ══ FIXED HEADER — 72px, transparent→frosted (Flighty exact) ══════════ */}
      <header
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 90,
          minHeight: HEADER_H,
          // Flighty exact: bg-top-bar = #fcfcfc, triggers at ~1px, 200ms ease-in-out
          background: `rgba(252,252,252,${headerProgress * 0.92})`,
          backdropFilter: headerProgress > 0.02 ? `blur(${Math.min(24, headerProgress * 32)}px)` : 'none',
          WebkitBackdropFilter: headerProgress > 0.02 ? `blur(${Math.min(24, headerProgress * 32)}px)` : 'none',
          borderBottom: `1px solid rgba(0,0,0,${headerProgress * 0.06})`,
          transition: 'background 0.2s ease-in-out, border-color 0.2s ease-in-out',
          display: 'flex',
          alignItems: 'center',
          padding: '0 20px',
          justifyContent: 'space-between',
        }}
      >
        {/* Left: wordmark */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <div style={S.logoMark}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="14" viewBox="0 0 31 24" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M17.4232 5.41484C16.6753 4.3061 16.6692 4.31217 15.7284 5.25292L15.7284 5.25293L0.676797 20.1894C-0.24242 21.1086 -0.24237 21.1087 0.781835 21.8377L0.797857 21.8491C1.06602 22.04 1.3416 22.2189 1.62386 22.3853L8.56262 15.4466L8.77013 16.5808L2.49905 22.8518C2.84577 23.0179 3.20066 23.1666 3.56253 23.2974L8.56262 18.2973L8.77013 19.4315L4.5864 23.6153C6.19334 24.0345 7.90833 24.1176 9.63516 23.8326C11.0879 23.5897 12.4599 23.0634 13.7108 22.2943C19.8042 18.6108 21.055 10.7985 17.4232 5.41484Z" fill="white" />
              <path d="M30.3223 6.39559C29.7574 5.82891 29.7574 5.66698 29.717 5.50507C29.2731 2.34776 26.5292 0 23.3413 0C21.6867 0 20.1534 0.607178 18.9428 1.74057L18.055 2.63109C17.1425 3.50242 17.3853 3.18751 17.9743 3.96689C21.7674 8.98622 21.6062 16.1105 17.4497 20.9274C22.2762 22.0709 27.8608 16.4747 27.8608 10.8077C27.8608 9.87674 27.7397 8.94574 27.4976 8.05522C27.4169 7.81235 27.4976 7.56947 27.6187 7.36708C27.7801 7.16469 27.9819 7.04326 28.2643 7.04326H30.0802C30.914 7.04326 30.9131 6.98814 30.3223 6.39559Z" fill="white" />
            </svg>
          </div>
          <span
            style={{
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              lineHeight: 1,
              transition: 'color 0.2s ease-in-out',
              textShadow: headerProgress < 0.4 ? '0 1px 6px rgba(0,0,0,0.3)' : 'none',
            }}
          >
            <span style={{ color: headerTextColor }}>Finch</span>
            {firmName && (
              <span
                style={{
                  color: headerProgress < 0.4
                    ? 'rgba(255,255,255,0.55)'
                    : 'rgba(15,23,42,0.45)',
                  marginLeft: 6,
                  transition: 'color 0.2s ease-in-out',
                }}
              >
                {firmName}
              </span>
            )}
          </span>
        </div>

        {/* Center: search bar — absolutely centered in the header */}
        <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 'min(432px, 50vw)', pointerEvents: 'none' }}>
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            style={{
              width: '100%',
              height: 48,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '0 20px',
              borderRadius: 9999,
              border: 'none',
              cursor: 'pointer',
              pointerEvents: 'auto',
              fontFamily: INTAKY_FONT.family,
              fontSize: 15,
              fontWeight: 500,
              // Over map: white pill with thick shadow ring
              // Stitched: flatter, blends into frosted header
              background: stitched ? '#E8EAEE' : 'white',
              boxShadow: stitched
                ? 'none'
                : '0 0 0 4px rgba(0,0,0,0.1), 0 2px 12px rgba(0,0,0,0.06)',
              color: INTAKY_COLORS.textSecondary,
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <MagnifyingGlassIcon style={{ width: 18, height: 18, flexShrink: 0, color: INTAKY_COLORS.textSecondary }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Search leads...
            </span>
            <span className="intaky-cmd-k" style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, background: 'rgba(0,0,0,0.08)', color: 'rgba(0,0,0,0.5)', padding: '2px 8px', borderRadius: 9999, flexShrink: 0, display: 'none' }}>
              ⌘K
            </span>
          </button>
        </div>

        {/* Right: TV Mode button */}
        <a
          href={firmId ? `/external/intake/${firmId}/tv` : '/external/intake/tv'}
          style={{
            display: 'flex',
            height: 48,
            alignItems: 'center',
            gap: 8,
            padding: '0 20px',
            borderRadius: 9999,
            fontFamily: INTAKY_FONT.family,
            fontSize: 15,
            fontWeight: 500,
            color: 'white',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            background: stitched
              ? 'linear-gradient(to bottom, rgba(39,40,47,0.85), rgba(13,12,15,0.85))'
              : 'linear-gradient(to bottom, rgba(39,40,47,0.6), rgba(13,12,15,0.6))',
            boxShadow: stitched
              ? '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.06)'
              : '0 0 0 4px rgba(0,0,0,0.04), 0 2px 12px rgba(0,0,0,0.06)',
            backdropFilter: 'blur(8px)',
            transition: 'all 0.2s ease-in-out 0.1s',
            flexShrink: 0,
          }}
          className="intaky-tv-btn"
        >
          <TvIcon style={{ width: 18, height: 18 }} />
          <span className="intaky-tv-label">TV Mode</span>
        </a>
      </header>

      {/* ══ SCROLL CONTENT ══════════════════════════════════════════════════════ */}
      {/* pointer-events:none so mouse events reach the fixed map through the spacer */}
      <div style={{ position: 'relative', zIndex: 2, pointerEvents: 'none' }}>

        {/* ── Transparent spacer — map shows through ── */}
        <div style={{ minHeight: '50vh', background: 'transparent' }} />

        {/* ── Bottom-sheet card section ── */}
        <div style={{ position: 'relative', overflowX: 'clip' }}>

          {/* Absolute white card background — expands at stitch threshold. */}
          <div
            style={{
              position: 'absolute',
              top: stitched ? -HEADER_H : 0,
              right: cardMargin,
              bottom: 0,
              left: cardMargin,
              borderTopLeftRadius: cardRadius,
              borderTopRightRadius: cardRadius,
              background: '#FFFFFF',
              boxShadow: stitched ? 'none' : BOTTOM_SHEET_SHADOW,
              transition: 'top 0.1s ease-in-out, right 0.1s ease-in-out, left 0.1s ease-in-out, border-radius 0.1s ease-in-out, box-shadow 0.1s ease-in-out',
            }}
          />

          {/* Card content */}
          <div
            style={{
              position: 'relative',
              zIndex: 10,
              paddingLeft: 60,
              paddingRight: 60,
              paddingTop: 35,
              paddingBottom: 40,
              pointerEvents: 'auto',
            }}
          >
            {/* Title row */}
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 4 }}>
              <div>
                <h1 style={{ margin: 0, fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.0, color: INTAKY_COLORS.textPrimary }}>
                  Active Leads & Activity
                </h1>
                {/* Last updated — prominent, with pulse animation on fresh updates */}
                <button
                  type="button"
                  onClick={() => setShowGlobalActivity(true)}
                  style={{
                    margin: '8px 0 0',
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    fontFamily: INTAKY_FONT.family,
                    color: lastUpdatedText === 'Updated just now'
                      ? INTAKY_COLORS.liveGreen
                      : INTAKY_COLORS.textMuted,
                    transition: 'color 0.15s ease',
                    animation: lastUpdatedText === 'Updated just now'
                      ? 'intaky-updated-pulse 1s ease-in-out'
                      : undefined,
                    letterSpacing: '-0.01em',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  {lastUpdatedText || 'Waiting for updates...'}
                  <span style={{ fontSize: 10, opacity: 0.5 }}>→</span>
                </button>
              </div>
              {/* Flighty-style tab switcher with Live indicator */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    height: 38,
                    borderRadius: 9999,
                    background: '#F1F3F5',
                    padding: 3,
                    fontFamily: INTAKY_FONT.family,
                  }}
                >
                  {/* Sliding white pill behind active tab */}
                  <span
                    style={{
                      position: 'absolute',
                      height: 32,
                      width: 'calc(50% - 3px)',
                      borderRadius: 9999,
                      background: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
                      transition: 'transform 0.2s ease-in-out',
                      transform: 'translateX(3px)',
                      top: 3,
                      left: 0,
                    }}
                  />
                  {/* Live tab */}
                  <span
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '0 14px',
                      fontSize: 12,
                      fontWeight: 600,
                      color: INTAKY_COLORS.textPrimary,
                      whiteSpace: 'nowrap',
                      zIndex: 1,
                    }}
                  >
                    <span style={{ position: 'relative', display: 'inline-flex', width: 8, height: 8 }}>
                      {connected && (
                        <span
                          style={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            background: connected ? INTAKY_COLORS.liveGreen : '#94A3B8',
                            opacity: 0.3,
                            animation: 'intaky-ping 1.4s cubic-bezier(0,0,0.2,1) infinite',
                          }}
                        />
                      )}
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: connected ? INTAKY_COLORS.liveGreen : '#94A3B8' }} />
                    </span>
                    Live
                  </span>
                  {/* Leads count tab */}
                  <span
                    style={{
                      position: 'relative',
                      padding: '0 14px',
                      fontSize: 12,
                      fontWeight: 500,
                      color: INTAKY_COLORS.textMuted,
                      whiteSpace: 'nowrap',
                      zIndex: 1,
                    }}
                  >
                    {intakes.length} leads
                  </span>
                </div>
              </div>
            </div>

            {/* Table */}
            <IntakyIntakeTable
              intakes={intakes}
              flashedIntakeIds={flashedIntakeIds}
              onIntakeClick={(intake) => setDrawerIntake(intake)}
            />
          </div>
        </div>


      </div>

      {/* ══ SEARCH DIALOG ══════════════════════════════════════════════════════ */}
      {searchOpen && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          onClick={(e) => { if (e.target === e.currentTarget) { setSearchOpen(false); setSearchQuery(''); } }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 200,
            background: 'rgba(0,0,0,0.3)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: 100,
            animation: 'intaky-search-backdrop 0.1s ease-out both',
          }}
        >
          <div
            style={{
              width: 520,
              maxWidth: '90vw',
              background: 'white',
              borderRadius: 16,
              boxShadow: '0 16px 70px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
              fontFamily: INTAKY_FONT.family,
              overflow: 'hidden',
              animation: 'intaky-search-in 0.15s ease-out both',
            }}
          >
            {/* Search input */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: `1px solid ${INTAKY_COLORS.border}` }}>
              <MagnifyingGlassIcon style={{ width: 18, height: 18, flexShrink: 0, color: INTAKY_COLORS.textMuted }} />
              <input
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                type="text"
                placeholder="Search leads by name, case type, location..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSearchIndex(0); }}
                onKeyDown={(e) => {
                  const q = searchQuery.toLowerCase().trim();
                  const results = q
                    ? intakes.filter((i) =>
                        i.client_display.toLowerCase().includes(q)
                        || i.case_type.toLowerCase().includes(q)
                        || i.state.toLowerCase().includes(q)
                        || (i.city ?? '').toLowerCase().includes(q)
                        || (i.referral_source ?? '').toLowerCase().includes(q),
                      ).slice(0, 20)
                    : intakes.slice(0, 8);

                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSearchIndex((prev) => Math.min(prev + 1, results.length - 1));
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSearchIndex((prev) => Math.max(prev - 1, 0));
                  } else if (e.key === 'Enter' && results.length > 0) {
                    e.preventDefault();
                    const selected = results[searchIndex];
                    if (selected) {
                      setDrawerIntake(selected);
                      setSearchOpen(false);
                      setSearchQuery('');
                      setSearchIndex(0);
                    }
                  }
                }}
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  fontSize: 15,
                  fontWeight: 500,
                  fontFamily: INTAKY_FONT.family,
                  color: INTAKY_COLORS.textPrimary,
                  background: 'transparent',
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  style={{ background: '#F1F5F9', border: 'none', borderRadius: 6, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 12, color: INTAKY_COLORS.textMuted }}
                >
                  ×
                </button>
              )}
              <span style={{ fontSize: 10, fontWeight: 600, color: INTAKY_COLORS.textMuted, background: '#F1F5F9', padding: '2px 8px', borderRadius: 4, flexShrink: 0 }}>ESC</span>
            </div>

            {/* Results */}
            <div style={{ maxHeight: 360, overflow: 'auto', padding: '4px 0' }}>
              {(() => {
                const q = searchQuery.toLowerCase().trim();
                const searchResults = q
                  ? intakes.filter((i) =>
                      i.client_display.toLowerCase().includes(q)
                      || i.case_type.toLowerCase().includes(q)
                      || i.state.toLowerCase().includes(q)
                      || (i.city ?? '').toLowerCase().includes(q)
                      || (i.referral_source ?? '').toLowerCase().includes(q),
                    ).slice(0, 20)
                  : intakes.slice(0, 8);

                if (searchResults.length === 0) {
                  return (
                    <div style={{ padding: '24px 18px', textAlign: 'center', color: INTAKY_COLORS.textMuted, fontSize: 13 }}>
                      {q ? `No leads matching "${searchQuery}"` : 'No leads available'}
                    </div>
                  );
                }

                return searchResults.map((intake, idx) => {
                  const tc = statusToTierConfig(intake.status);
                  const isSelected = idx === searchIndex;
                  return (
                    <button
                      key={intake.id}
                      type="button"
                      onClick={() => { setDrawerIntake(intake); setSearchOpen(false); setSearchQuery(''); setSearchIndex(0); }}
                      onMouseEnter={() => setSearchIndex(idx)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 18px',
                        border: 'none',
                        background: isSelected ? '#F1F5F9' : 'transparent',
                        cursor: 'pointer',
                        fontFamily: INTAKY_FONT.family,
                        textAlign: 'left',
                        transition: 'background 0.05s',
                      }}
                    >
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: tc.dot, flexShrink: 0 }} />
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: INTAKY_COLORS.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {intake.client_display}
                        </div>
                        <div style={{ fontSize: 11, color: INTAKY_COLORS.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {[intake.case_type, intake.state && (intake.city ? `${intake.city}, ${intake.state}` : intake.state), intake.referral_source].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    </button>
                  );
                });
              })()}
            </div>

            {/* Footer hint */}
            <div style={{ padding: '8px 18px', borderTop: `1px solid ${INTAKY_COLORS.border}`, display: 'flex', gap: 12, fontSize: 10, color: INTAKY_COLORS.textMuted }}>
              <span>↑↓ Navigate</span>
              <span>↵ Open</span>
              <span>ESC Close</span>
            </div>
          </div>
        </div>
      )}

      {/* ══ ACTIVITY DRAWER ═══════════════════════════════════════════════════ */}
      {drawerIntake && (
        <IntakyActivityDrawer
          intake={drawerIntake}
          firmId={firmId}
          version={activityVersion}
          onClose={() => setDrawerIntake(null)}
        />
      )}

      {showGlobalActivity && (
        <IntakyGlobalActivityDrawer
          activities={allActivities}
          intakeNames={Object.fromEntries(intakes.map((i) => [i.id, i.client_display]))}
          onClose={() => setShowGlobalActivity(false)}
        />
      )}
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const S: Record<string, React.CSSProperties> = {
  centeredScreen: {
    minHeight: '100vh',
    width: '100vw',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#F8FAFC',
    fontFamily: INTAKY_FONT.family,
  },

  spinner: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: `2.5px solid ${INTAKY_COLORS.border}`,
    borderTopColor: INTAKY_COLORS.liveGreen,
    animation: 'intaky-spin 0.75s linear infinite',
  },

  logoMark: {
    width: 26,
    height: 26,
    borderRadius: 7,
    background: INTAKY_COLORS.brand,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 1px 4px rgba(0,0,0,0.24)',
  },
};
